import React from 'react'
import { observer } from 'mobx-react-lite'
import clsx from 'clsx'

import { Box, FormHelperText, FormLabel, Grid, IconButton } from '@material-ui/core'

import addIcon from '../assets/addIcon.svg'
import arrowRight from '../assets/arrowRight.svg'
import deleteIcon from '../assets/deleteIcon.svg'

import { BootstrapInput } from './BootstrapInput'
import { WarehousePicker } from './WarehousePicker'
import { MoySkladStorePicker } from './MoySkladStorePicker'
import { StoresFormBaseProps, useStoresFormStyles } from './StoresFormDirect'

import { useLogic } from '../hooks/storeHook'
import { useMessages } from '../hooks/snackbarHooks'
import { useDefaultMenuItemStyles } from '../hooks/defaultSelectStylesHook'

import { EditStoreModel } from '../server/mpsklad_core/Models/EditStoreModel'
import { StoreModelBase } from '../server/mpsklad_core/Models/StoreModelBase'
import { WarehouseModel } from '../server/mpsklad_core/Models/WarehouseModel'
import { MoySkladStoreModel } from '../server/mpsklad_core/Models/MoySkladStoreModel'
import { StoreRelationType } from '../server/mpsklad_core/Entity/Base/StoreRelationType'
import { EditSimpleStoresModel } from '../server/mpsklad_core/Models/EditSimpleStoresModel'
import { integrationTypeNames } from '../types/integrationTypeUtils'

/**
 * See {@link StoreRelationType.AllToOneMoySklad}.
 */
export const StoresFormAllToOneMoySklad =
  observer(
    <TStore extends StoreModelBase, TEditStore extends EditStoreModel, TWarehouse extends WarehouseModel>
    ({
       disabled,
       storeType,
       serverData, formData, initStoreCargoType,
       whKeySelector, storeWhKeySelector,
       formatWarehouseDisplayName
     }: StoresFormBaseProps<TStore, TEditStore, TWarehouse>) => {
      const classes = useStoresFormStyles()
      const menuClasses = useDefaultMenuItemStyles()

      const {showWarning} = useMessages()

      const {showDialog} = useLogic()

      const storeTypeName = integrationTypeNames[storeType]

      if (formData.relationType !== StoreRelationType.AllToOneMoySklad) {
        throw new Error(`Bad relation type: ${formData.relationType}!`)
      }

      if (serverData.warehouses.length === 0) {
        return <FormHelperText error>Нет складов {storeTypeName}.</FormHelperText>
      }

      if (serverData.msStores.length === 0) {
        return <FormHelperText error>Нет складов МоегоСклада.</FormHelperText>
      }

      const msStoreId = tryGetMsStoreId(formData)

      const unmatchedWarehouse = serverData.warehouses.find(
        warehouse => formData.stores.every(store => storeWhKeySelector(store) !== whKeySelector(warehouse)))

      const onAddStore =
        () => {
          if (!unmatchedWarehouse) {
            showWarning(`Все склады ${storeTypeName} сопоставлены!`)
            return
          }

          formData.stores.push(
            initStoreCargoType(unmatchedWarehouse, {
              storeId: undefined,
              warehouseId: unmatchedWarehouse.id,
              warehouseName: unmatchedWarehouse.name,
              msStoreIds: msStoreId ? [msStoreId] : [/* Allow import into MS */]
            }))
        }

      const onDeleteStore =
        async (formStore: EditStoreModel) => {
          if (formStore.storeId != null && !await showDialog(
            `Заказы для склада ${storeTypeName} будут удалены из MPsklad после сохранения!`, {
              title: 'Удалить сопоставление складов?',
              acceptButton: 'Удалить'
            })) {
            return
          }

          formData.stores = formData.stores.filter(_ => _ !== formStore)
        }

      const onChangeWarehouse =
        (formStore: EditStoreModel, warehouse: TWarehouse) => {
          formStore.warehouseId = warehouse.id
          formStore.warehouseName = warehouse.name
        }

      const onChangeMsStore =
        (newMsStore: MoySkladStoreModel | null) => {
          for (const formStore of formData.stores) {
            formStore.msStoreIds = newMsStore ? [newMsStore.id] : []
          }
        }

      return (
        <Grid container direction="column">
          <Grid item xs={12}>
            <Grid container>
              <Grid item xs={5}>
                <FormLabel component="legend" className={classes.label}>
                  <span className={classes.dash}>&mdash;</span>
                  Склад МоегоСклада
                </FormLabel>
              </Grid>

              <Grid item xs={6}>
                <FormLabel component="legend" className={classes.label}>
                  <span className={classes.dash}>&mdash;</span>
                  Склад {storeTypeName}
                </FormLabel>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Grid container alignItems="center">
              <Grid item xs={4}>
                <MoySkladStorePicker
                  allowEmpty
                  filterMatchedStores
                  disabled={formData.stores.length === 0}
                  msStoreId={msStoreId}
                  msStores={serverData.msStores}
                  formData={formData}
                  onChange={onChangeMsStore}
                />
              </Grid>

              <Grid item xs={1}>
                <Box display="flex" height="100%" justifyContent="center" alignItems="center">
                  <img src={arrowRight} alt="" style={{width: 20, height: 20}}/>
                </Box>
              </Grid>

              <Grid item xs={5}>
                <Grid container spacing={3}>
                  {
                    formData.stores.map(
                      (formStore, index) =>
                        <Grid item xs={12} key={index}>
                          <Grid container spacing={1}>
                            <Grid item xs={10}>
                              <WarehousePicker
                                formStore={formStore}
                                formData={formData}
                                warehouses={serverData.warehouses}
                                whKeySelector={whKeySelector}
                                storeWhKeySelector={storeWhKeySelector}
                                formatWarehouseDisplayName={formatWarehouseDisplayName}
                                onChange={warehouse => onChangeWarehouse(formStore, warehouse)}
                              />
                            </Grid>

                            <Grid item xs={2}>
                              <IconButton
                                title="Удалить"
                                disabled={disabled}
                                className={clsx(menuClasses.iconButtonOutlined)}
                                onClick={() => onDeleteStore(formStore)}
                              >
                                <img src={deleteIcon} alt=""/>
                              </IconButton>
                            </Grid>
                          </Grid>
                        </Grid>
                    )
                  }

                  {
                    !!unmatchedWarehouse &&
                    <Grid item xs={12}>
                      <Grid container spacing={1}>
                        <Grid item xs={10}>
                          <BootstrapInput
                            readOnly
                            title="Добавить"
                            className={clsx(menuClasses.input, menuClasses.inputClickable)}
                            onClick={onAddStore}
                          />
                        </Grid>

                        <Grid item xs={2}>
                          <IconButton
                            title="Добавить"
                            disabled={disabled}
                            className={clsx(menuClasses.iconButtonOutlined)}
                            onClick={onAddStore}
                          >
                            <img src={addIcon} alt=""/>
                          </IconButton>
                        </Grid>
                      </Grid>
                    </Grid>
                  }
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )
    })

const tryGetMsStoreId =
  (formData: EditSimpleStoresModel): string | undefined => {
    const msStoreIds = new Set(formData.stores.flatMap(_ => _.msStoreIds))

    if (msStoreIds.size === 0) {
      return undefined
    }

    if (msStoreIds.size === 1) {
      return Array.from(msStoreIds)[0]
    }

    throw new Error(`Bad MS store count for AllToOneMoySklad: ${msStoreIds.size}.`)
  }